/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';
import { BrowserRouter as Router } from 'react-router-dom';

import AuthProvider from 'app/auth/helpers/AuthProvider';
import { EXCLUDED_BASE_PATHS } from 'app/common/constants/paths';
import { Environment } from 'app/common/services';
import { App, TenantGateway } from 'app/core/components';
import {
  CurrencyProvider,
  CustomerProvider,
  I18nProvider,
  PreviewProvider,
  ThemeProvider
} from 'app/core/helpers';

import messages from './messages';
import * as serviceWorker from './serviceWorker';

import './loadLocaleData';
import './loadIconData';

// imports all styles
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'styles/tailwind.css';
import 'paymentfont/css/paymentfont.css';
import { createRoot } from 'react-dom/client';
import { ContentModelProvider } from 'app/content/contexts/ContentModelContext.js';

const browserSupportsHistory = 'pushState' in window.history;

handleDealerSlug();

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
  <Router
    basename={Environment.get('public.url')}
    forceRefresh={!browserSupportsHistory}
  >
    <I18nProvider messages={messages}>
      <CurrencyProvider>
        <TenantGateway>
          <ThemeProvider>
            <AuthProvider>
              <CustomerProvider>
                <PreviewProvider>
                  <ContentModelProvider>
                    <App />
                  </ContentModelProvider>
                </PreviewProvider>
              </CustomerProvider>
            </AuthProvider>
          </ThemeProvider>
        </TenantGateway>
      </CurrencyProvider>
    </I18nProvider>
  </Router>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();

/**
 * Intercepts path-based dealer slugs and converts them to the query parameter
 * expected by the TenantGateway. This allows for URLs like /dealer1/cart to be
 * handled as /cart?locationId=dealer1.
 */
function handleDealerSlug() {
  const categoryPrefix = Environment.get(
    'CATEGORY_URL_PREFIX',
    '/browse'
  ).replace(/^\//, '');
  const productPrefix = Environment.get(
    'PRODUCT_URL_PREFIX',
    '/details'
  ).replace(/^\//, '');
  const brandsRootPath = Environment.get('brands.path.root', '/brands').replace(
    /^\//,
    ''
  );

  const excludedPaths = [
    ...EXCLUDED_BASE_PATHS,
    'api',
    'static',
    categoryPrefix,
    productPrefix,
    brandsRootPath
  ].filter(Boolean);

  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  const firstSegment = pathSegments[0];

  if (firstSegment && !excludedPaths.includes(firstSegment)) {
    const remainingPath = '/' + pathSegments.slice(1).join('/');
    const url = new URL(window.location.href);

    // Save the original path
    window.__originalPath = window.location.pathname;

    // Remove the dealer segment from the path
    url.pathname = remainingPath;

    // Append the dealer ID as the query parameter expected by TenantGateway
    const applicationParameter = getApplicationParameter();
    url.searchParams.set(applicationParameter, firstSegment);

    // Replace the current history state to update the URL without refreshing the page
    window.history.replaceState(null, '', url.toString());
  }

  function getApplicationParameter() {
    return Environment.get(
      'tenant.resolver.application.parameter',
      'application'
    );
  }
}
